import { usePaymentContext } from "@bokio/contexts/PaymentContext/PaymentContext";
import { useFeatureToggle } from "@bokio/hooks/useFeatureToggle/useFeatureToggle";
import { useNewSveaAuthentication } from "@bokio/mobile-web-shared/areas/bank/hooks/useNewSveaAuthentication";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { useActiveBankConnection } from "@bokio/mobile-web-shared/hooks/useActiveBankConnection/useActiveBankConnection";
import { useCompanyUser } from "@bokio/shared/state/requests";

import type { DelegateInformation } from "@bokio/mobile-web-shared/areas/bank/hooks/useNewSveaAuthentication";

export type BBASveaAuthenticationGateState =
	| { type: "disabled"; canSignAndPay: boolean } //The SveaDelegates feature toggle is disabled. canSignAndPay is a helper which can be used to determine old behaviour.
	| { type: "notPermitted" } //User doesn't have access to BBA at all (i.e. companyUserPermissions.BbaAccess is false)
	| { type: "unauthenticated" } //User has access to BBA but is not authenticated
	| {
			//User is authenticated. Besides general delegateInformation, current companys psu information is also provided (if available).
			type: "authenticated";
			delegateInformation: DelegateInformation;
			currentPsuInfo?: DelegateInformation["psus"][number] | null;
	  };

export const useBBASveaAuthenticationGate: () => BBASveaAuthenticationGateState = () => {
	const { companyUserPermissions } = useCompanyUser();
	const { companyInfo } = useCompanyInfo();
	const { isFeatureEnabled } = useFeatureToggle();
	const { hasActiveBusinessAccount } = useActiveBankConnection();
	const { currentUserPermission } = usePaymentContext();
	const auth = useNewSveaAuthentication();
	const authState = auth.useState();

	const canSignAndPay = hasActiveBusinessAccount && currentUserPermission.CanSignPayment;

	if (!isFeatureEnabled(m.Core.Features.SveaDelegates)) {
		return {
			type: "disabled",
			canSignAndPay,
		};
	}

	if (!companyUserPermissions?.BbaAccess) {
		return {
			type: "notPermitted",
		};
	}

	switch (authState.type) {
		case "authenticated":
			return {
				type: "authenticated",
				delegateInformation: authState.delegateInformation,
				currentPsuInfo: companyInfo.BBASveaPsuId
					? (authState.delegateInformation.psus.find(psu => psu.psuId === companyInfo.BBASveaPsuId) ?? null)
					: null,
			};
		case "unauthenticated":
			return {
				type: "unauthenticated",
			};
		default:
			throw new Error("Unknown authentication state");
	}
};
